import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Header, MobileNav } from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { ArrowLeft, Pause, Trash, Warning, ShieldCheck, UserCircle } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AccountSettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [suspending, setSuspending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const isSuspended = user?.suspended;

  const handleSuspend = async () => {
    setSuspending(true);
    try {
      const endpoint = isSuspended ? '/account/reactivate' : '/account/suspend';
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'PUT',
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        window.location.reload();
      } else {
        toast.error(data.detail || 'Erro ao processar');
      }
    } catch (error) {
      toast.error('Erro ao processar pedido');
    } finally {
      setSuspending(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirm !== 'APAGAR') return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/account`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Conta apagada com sucesso');
        await logout();
        navigate('/');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erro ao apagar conta');
      }
    } catch (error) {
      toast.error('Erro ao apagar conta');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="account-settings-page">
      <Header />
      <main className="container mx-auto px-4 py-6 pb-24 sm:pb-6 max-w-2xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-6">
            <Link to={user?.role === 'prestador' ? '/provider/dashboard' : '/client/home'}>
              <Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold font-outfit">Definições da Conta</h1>
              <p className="text-sm text-muted-foreground">Gerir o seu perfil e conta</p>
            </div>
          </div>

          {/* Account Info */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <UserCircle className="w-5 h-5" />
                Informações da Conta
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-muted-foreground">Nome</span>
                <span className="text-sm font-medium">{user?.name || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-muted-foreground">Email</span>
                <span className="text-sm font-medium">{user?.email || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-muted-foreground">Tipo de conta</span>
                <span className="text-sm font-medium">
                  {user?.role === 'prestador' ? 'Prestador' : user?.role === 'admin' ? 'Administrador' : 'Cliente'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-muted-foreground">Método de autenticação</span>
                <span className="text-sm font-medium">{user?.auth_method === 'google' ? 'Google' : 'Email OTP'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Suspend Account */}
          <Card className="mb-6 border-amber-200 dark:border-amber-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Pause className="w-5 h-5 text-amber-500" />
                {isSuspended ? 'Conta Suspensa' : 'Suspender Conta'}
              </CardTitle>
              <CardDescription>
                {isSuspended
                  ? 'A sua conta está suspensa. O seu perfil está oculto e não receberá novos pedidos.'
                  : 'Suspender a conta oculta o seu perfil temporariamente. Os seus dados são mantidos e pode reativar a qualquer momento.'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant={isSuspended ? 'default' : 'outline'}
                onClick={handleSuspend}
                disabled={suspending}
                className="rounded-full"
                data-testid="suspend-account-btn"
              >
                {suspending ? 'A processar...' : isSuspended ? (
                  <><ShieldCheck className="w-4 h-4 mr-2" /> Reativar Conta</>
                ) : (
                  <><Pause className="w-4 h-4 mr-2" /> Suspender Conta</>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Delete Account */}
          <Card className="border-red-200 dark:border-red-900">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base text-destructive">
                <Trash className="w-5 h-5" />
                Apagar Conta
              </CardTitle>
              <CardDescription>
                Esta ação é irreversível. Todos os seus dados, incluindo perfil, animais, mensagens, avaliações e histórico de pagamentos serão permanentemente apagados.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogTrigger asChild>
                  <Button variant="destructive" className="rounded-full" data-testid="delete-account-btn">
                    <Trash className="w-4 h-4 mr-2" /> Apagar Conta Permanentemente
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-destructive">
                      <Warning weight="fill" className="w-5 h-5" />
                      Confirmar Eliminação
                    </DialogTitle>
                    <DialogDescription>
                      Esta ação não pode ser desfeita. Para confirmar, escreva <strong>APAGAR</strong> no campo abaixo.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <Input
                      placeholder="Escreva APAGAR para confirmar"
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      data-testid="delete-confirm-input"
                    />
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="flex-1 rounded-full">
                        Cancelar
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={deleteConfirm !== 'APAGAR' || deleting}
                        className="flex-1 rounded-full"
                        data-testid="confirm-delete-btn"
                      >
                        {deleting ? 'A apagar...' : 'Apagar Definitivamente'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </motion.div>
      </main>
      <MobileNav />
    </div>
  );
}
